<script setup>
import { useHR } from '@/composables/useHR'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'credential-manage',
    subject: 'Hr',
  },
})

const route = useRoute('hr-employees-id-credential')
const router = useRouter()
const ability = useAbility()
const { saving, updateCredential, createLoginAccount, resetLoginPassword } = useHR()

const employee = ref(null)
const loading = ref(false)
const form = ref({ str_expiry_date: '', sip_expiry_date: '' })
const error = ref('')

const fetchEmployee = async () => {
  loading.value = true
  try {
    const response = await useApi(`/api/v1/master-data/employees/${route.params.id}`).get()

    employee.value = response?.data ?? response
    form.value = {
      str_expiry_date: employee.value.str_expiry_date ?? '',
      sip_expiry_date: employee.value.sip_expiry_date ?? '',
    }
  } finally {
    loading.value = false
  }
}

onMounted(fetchEmployee)

// ---------------------------------------------------------------------
// Akun login — hilangkan input dobel Employee/User, lihat useHR.js.
// ---------------------------------------------------------------------
const roles = ref([])
const roleItems = computed(() => roles.value.map(r => ({ title: r.name, value: r.name })))

const fetchRoles = async () => {
  try {
    const response = await useApi('/api/v1/roles').get()
    const data = response?.data ?? response

    roles.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error('Fetch roles error:', e)
  }
}

onMounted(fetchRoles)

const createLoginDialog = ref(false)
const createLoginRoles = ref([])
const createLoginError = ref('')

const generatedPasswordDialog = ref(false)
const generatedPasswordInfo = ref({ email: '', password: '' })

const openCreateLoginDialog = () => {
  createLoginRoles.value = []
  createLoginError.value = ''
  createLoginDialog.value = true
}

const submitCreateLogin = async () => {
  createLoginError.value = ''
  try {
    const response = await createLoginAccount(employee.value.id, createLoginRoles.value)

    employee.value = response?.data ?? response
    createLoginDialog.value = false
    generatedPasswordInfo.value = { email: employee.value?.user?.email ?? employee.value?.email ?? '', password: response?.generated_password ?? '' }
    generatedPasswordDialog.value = true
  } catch (e) {
    createLoginError.value = e?.data?.message ?? 'Gagal membuat akun login.'
  }
}

const resetPasswordConfirmDialog = ref(false)

const submitResetPassword = async () => {
  try {
    const response = await resetLoginPassword(employee.value.id)

    resetPasswordConfirmDialog.value = false
    generatedPasswordInfo.value = { email: employee.value?.user?.email ?? '', password: response?.generated_password ?? '' }
    generatedPasswordDialog.value = true
  } catch (e) {
    error.value = e?.data?.message ?? 'Gagal mereset password.'
  }
}

const save = async () => {
  error.value = ''
  try {
    // Kirim string kosong sebagai null (mengosongkan tanggal berlaku
    // sengaja diperbolehkan — BR-007 otomatis menghentikan reminder aktif
    // lewat App\Traits\HasReminder saat field-nya berubah jadi null).
    employee.value = await updateCredential(route.params.id, {
      str_expiry_date: form.value.str_expiry_date || null,
      sip_expiry_date: form.value.sip_expiry_date || null,
    })
  } catch (e) {
    error.value = e?.data?.message ?? 'Gagal menyimpan kredensial.'
  }
}
</script>

<template>
  <div>
    <h4 class="text-h4 font-weight-bold mb-1">
      Kredensial Pegawai
    </h4>
    <p class="text-body-2 text-medium-emphasis mb-4">
      {{ employee?.name }} — {{ employee?.employee_number }}
    </p>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-else-if="employee">
      <VRow>
        <VCol
          cols="12"
          md="6"
        >
          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0 d-flex align-center justify-space-between">
              STR
              <ReminderBadge
                remindable-type="employee"
                :remindable-id="employee.id"
                context="str"
              />
            </VCardTitle>
            <VCardText class="pa-6">
              <VAlert
                v-if="error"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4"
              >
                {{ error }}
              </VAlert>
              <VTextField
                v-model="form.str_expiry_date"
                label="Tanggal Berlaku STR"
                type="date"
              />
            </VCardText>
          </VCard>

          <VCard>
            <VCardTitle class="pa-6 pb-0 d-flex align-center justify-space-between">
              SIP
              <ReminderBadge
                remindable-type="employee"
                :remindable-id="employee.id"
                context="sip"
              />
            </VCardTitle>
            <VCardText class="pa-6">
              <VTextField
                v-model="form.sip_expiry_date"
                label="Tanggal Berlaku SIP"
                type="date"
              />
            </VCardText>
            <VCardActions class="pa-6 pt-0">
              <VSpacer />
              <VBtn
                variant="tonal"
                color="secondary"
                :disabled="saving"
                @click="router.push({ name: 'hr-employees' })"
              >
                Kembali
              </VBtn>
              <VBtn
                color="primary"
                :loading="saving"
                @click="save"
              >
                Simpan
              </VBtn>
            </VCardActions>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="6"
        >
          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Akun Login
            </VCardTitle>
            <VCardText class="pa-6">
              <template v-if="employee.user_id">
                <VAlert
                  type="success"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                >
                  Akun login: aktif ({{ employee.user?.email }})
                </VAlert>
                <VBtn
                  v-if="ability.can('update', 'User')"
                  variant="tonal"
                  color="warning"
                  prepend-icon="ri-lock-password-line"
                  @click="resetPasswordConfirmDialog = true"
                >
                  Reset Password
                </VBtn>
              </template>
              <template v-else>
                <VAlert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                >
                  Pegawai ini belum punya akun login.
                </VAlert>
                <VBtn
                  v-if="ability.can('create', 'User')"
                  color="primary"
                  prepend-icon="ri-user-add-line"
                  @click="openCreateLoginDialog"
                >
                  Buatkan Akun Login
                </VBtn>
              </template>
            </VCardText>
          </VCard>

          <VCard>
            <VCardTitle class="pa-6 pb-0">
              Dokumen Kredensial
            </VCardTitle>
            <VCardText class="pa-6">
              <EvidenceUploader
                evidenceable-type="employee"
                :evidenceable-id="employee.id"
              />
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>

    <VDialog
      v-model="createLoginDialog"
      max-width="480"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Buatkan Akun Login
        </VCardTitle>
        <VCardText class="pa-6">
          <VAlert
            v-if="createLoginError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ createLoginError }}
          </VAlert>
          <p class="mb-4">
            Nama & email akun login otomatis memakai data pegawai ini ({{ employee?.name }} — {{ employee?.email }}). Password akan dibuatkan otomatis.
          </p>
          <VSelect
            v-model="createLoginRoles"
            :items="roleItems"
            label="Role *"
            multiple
            chips
            closable-chips
          />
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="createLoginDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            :disabled="!createLoginRoles.length"
            @click="submitCreateLogin"
          >
            Buat Akun
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="resetPasswordConfirmDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Reset Password?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          Password baru akan dibuatkan secara acak untuk akun <strong>{{ employee?.user?.email }}</strong>, dan pegawai wajib menggantinya saat login berikutnya.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="resetPasswordConfirmDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="warning"
            :loading="saving"
            @click="submitResetPassword"
          >
            Reset Password
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="generatedPasswordDialog"
      max-width="480"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Password Akun Login
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <VAlert
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            Catat password ini sekarang — tidak akan ditampilkan lagi setelah dialog ini ditutup.
          </VAlert>
          <p class="mb-1">
            <strong>Email:</strong> {{ generatedPasswordInfo.email }}
          </p>
          <p class="mb-0">
            <strong>Password:</strong> <code>{{ generatedPasswordInfo.password }}</code>
          </p>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            color="primary"
            @click="generatedPasswordDialog = false"
          >
            Tutup
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
